import { SignOutButton, useAuth, useUser } from "@clerk/clerk-react";
import { useEffect, useRef, useState } from "react";
import { Button } from "@/components/ui/button";
import {
  claimMemberEligibility,
  type EligibilityClaimResult,
} from "@/lib/eligibilityClaim";
import { hasMemberAccess } from "@/lib/memberAccess";

const MEMBER_PRODUCT = "etp";

export function MemberAccessGate({ children }: { children: React.ReactNode }) {
  const { getToken } = useAuth();
  const { user, isLoaded } = useUser();
  const alreadyEntitled = Boolean(
    isLoaded && user && hasMemberAccess(user.publicMetadata),
  );
  const claim = useMemberEligibilityClaim({
    shouldClaim: isLoaded && Boolean(user) && !alreadyEntitled,
    user,
    getToken,
  });

  if (!isLoaded || !user) {
    return <MemberGateShell />;
  }
  if (alreadyEntitled || claim.granted) {
    return <>{children}</>;
  }
  if (!claim.settled) {
    return <MemberGateShell />;
  }

  return (
    <MemberGateShell>
      <h1 className="text-2xl font-semibold tracking-refined text-foreground">
        Your account doesn&rsquo;t include the Member Portal
      </h1>
      <p className="mt-3 max-w-md text-sm leading-6 text-muted-foreground">
        You&rsquo;re signed in, but this private portal is available only to ETP
        members. If you were expecting access, ask the team to add ETP to your
        account.
      </p>
      <SignOutButton>
        <Button variant="outline" className="mt-6">
          Sign out
        </Button>
      </SignOutButton>
    </MemberGateShell>
  );
}

type ClaimableUser = {
  reload: () => Promise<unknown>;
};

type TokenGetter = (options?: {
  skipCache?: boolean;
  template?: string;
}) => Promise<string | null>;

function useMemberEligibilityClaim({
  shouldClaim,
  user,
  getToken,
}: {
  shouldClaim: boolean;
  user: ClaimableUser | null | undefined;
  getToken: TokenGetter;
}): { settled: boolean; granted: boolean } {
  const attempted = useRef(false);
  const [state, setState] = useState({ settled: false, granted: false });

  useEffect(() => {
    if (!shouldClaim || !user || attempted.current) return;
    attempted.current = true;

    void (async () => {
      try {
        const token = await getToken();
        const result: EligibilityClaimResult = token
          ? await claimMemberEligibility({ token })
          : { eligible: false, products: [] };
        const granted =
          result.eligible && result.products.includes(MEMBER_PRODUCT);

        if (granted) {
          await user.reload();
          await getToken({ template: "convex", skipCache: true });
        }
        setState({ settled: true, granted });
      } catch (error) {
        console.error("Member Portal eligibility claim failed", error);
        setState({ settled: true, granted: false });
      }
    })();
  }, [getToken, shouldClaim, user]);

  return state;
}

function MemberGateShell({ children }: { children?: React.ReactNode }) {
  return (
    <div className="min-h-screen bg-gradient-to-br from-background via-background to-muted/30 px-4">
      <div className="mx-auto flex min-h-screen max-w-2xl flex-col items-center justify-center text-center">
        <div className="mb-8 flex items-center gap-2.5">
          <img
            src="/etp-icon.svg"
            alt="ETP Executive Network"
            className="h-9 w-9"
          />
          <div className="text-left">
            <p className="text-xl font-semibold tracking-refined text-foreground">
              ETP Executive Network
            </p>
            <p className="text-xs font-medium uppercase tracking-elegant text-muted-foreground">
              Member Portal
            </p>
          </div>
        </div>
        {children ?? (
          <p className="text-sm text-muted-foreground" aria-busy="true">
            Verifying your member access&hellip;
          </p>
        )}
      </div>
    </div>
  );
}
